/**
 * Portal Module - Main Orchestrator
 * Initializes all portal controllers and handles app rendering
 */
import { PortalStorageService } from './storage_service';
import { SearchController } from './search_controller';
import { FavoritesController } from './favorites_controller';
import { RecentlyUsedController } from './recently_used_controller';
import { SectionController } from './section_controller';
import { ViewController } from './view_controller';

const DEFAULT_LOGO_PATH = '/packs/static/images';
const APP_TYPE_CORE = 'core';

class PortalApp {
  constructor() {
    this.storage = null;
    this.searchController = null;
    this.favoritesController = null;
    this.recentlyUsedController = null;
    this.sectionController = null;
    this.viewController = null;
    this.apps = [];
    this.userId = null;
    this.faviconServiceUrl = null;
    this.faviconSize = null;
    this.appCardTemplate = null;
    this.emptyStateTemplate = null;
  }

  async init() {
    const dataEl = document.getElementById('portal-data');
    if (!dataEl) return; // Not on portal page

    // Parse data
    this.userId = dataEl.dataset.userId;
    this.faviconServiceUrl = dataEl.dataset.faviconServiceUrl;
    this.faviconSize = dataEl.dataset.faviconSize;

    // Get templates
    this.appCardTemplate = document.getElementById('app-card-template');
    this.emptyStateTemplate = document.getElementById('empty-state-template');

    try {
      this.apps = JSON.parse(dataEl.dataset.apps || '[]');
    } catch (e) {
      console.error('Failed to parse portal apps data:', e);
      this.apps = [];
    }

    if (!this.userId) {
      console.error('Portal: No user ID found');
      return;
    }

    // Initialize storage service
    this.storage = new PortalStorageService(this.userId);

    // Clean up stale data (apps that no longer exist)
    const validAppIds = this.apps.map(app => app.sso_url);
    await this.storage.cleanupStaleData(validAppIds);

    // Initialize controllers
    this.searchController = new SearchController();

    this.favoritesController = new FavoritesController(this.storage, {
      onFavoritesChange: (favorites, appId, added) => this._handleFavoritesChange(favorites, appId, added)
    });

    this.recentlyUsedController = new RecentlyUsedController(this.storage, {
      onRecentlyUsedChange: (recentlyUsed, appId) => this._handleRecentlyUsedChange(recentlyUsed, appId)
    });

    this.sectionController = new SectionController(this.storage);
    this.viewController = new ViewController(this.storage);

    // Initialize all controllers
    await Promise.all([
      this.favoritesController.init(),
      this.recentlyUsedController.init(),
      this.sectionController.init(),
      this.viewController.init()
    ]);

    // Render sections (enhances server-rendered content)
    this._renderSections();

    // Initialize search last (after sections are rendered)
    this.searchController.init();

    // Show the JS-only UI elements
    this._showEnhancedUI();

    // Initialize dropdown menu behavior
    this._initDropdown();
  }

  _initDropdown() {
    const trigger = document.getElementById('user-profile-button');
    const menu = document.getElementById('user-dropdown-menu');
    if (!trigger || !menu) return;

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = menu.classList.contains('open');
      menu.classList.toggle('open');
      trigger.setAttribute('aria-expanded', String(!isOpen));
    });

    document.addEventListener('click', (e) => {
      if (!menu.contains(e.target) && !trigger.contains(e.target)) {
        menu.classList.remove('open');
        trigger.setAttribute('aria-expanded', 'false');
      }
    });

    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        trigger.click();
      }
    });
  }

  _showEnhancedUI() {
    // Show search bar
    const searchBar = document.querySelector('.portal-search-container');
    if (searchBar) searchBar.style.display = '';

    // Show view toggle
    const viewToggle = document.querySelector('.portal-controls');
    if (viewToggle) viewToggle.style.display = '';

    // Show favorites section
    const favoritesSection = document.querySelector('.portal-section[data-section-id="favorites"]');
    if (favoritesSection) favoritesSection.style.display = '';

    // Show all apps section header
    const allAppsHeader = document.querySelector('.portal-section[data-section-id="allApps"] .section-header');
    if (allAppsHeader) allAppsHeader.style.display = '';

    // Show favorite buttons on existing cards
    document.querySelectorAll('.favorite-btn').forEach(btn => {
      btn.style.display = '';
    });
  }

  _renderSections() {
    this._renderRecentlyUsedSection();
    this._renderFavoritesSection();
    this._renderAllAppsSection();
  }

  _renderRecentlyUsedSection() {
    const container = document.getElementById('recently-used-apps');
    if (!container) return;

    const recentlyUsedIds = this.recentlyUsedController.getRecentlyUsedAppIds();
    const section = document.querySelector('.portal-section[data-section-id="recentlyUsed"]');

    // Hide section if no recently used apps
    if (recentlyUsedIds.length === 0) {
      if (section) section.classList.add('hidden');
      return;
    }

    if (section) section.classList.remove('hidden');

    // Get apps in recently used order
    const recentApps = recentlyUsedIds
      .map(id => this.apps.find(app => app.sso_url === id))
      .filter(Boolean);

    container.innerHTML = '';
    recentApps.forEach(app => {
      container.appendChild(this._createAppCard(app));
    });
  }

  _renderFavoritesSection() {
    const container = document.getElementById('favorites-apps');
    if (!container) return;

    const favoriteIds = this.favoritesController.getFavorites();
    const emptyState = container.querySelector('.portal-empty-state');

    // Get favorite apps
    const favoriteApps = favoriteIds
      .map(id => this.apps.find(app => app.sso_url === id))
      .filter(Boolean);

    // Remove existing app cards (keep the empty state element)
    container.querySelectorAll('.app-card-wrapper').forEach(el => el.remove());

    if (favoriteApps.length === 0) {
      if (emptyState) emptyState.style.display = '';
    } else {
      if (emptyState) emptyState.style.display = 'none';
      favoriteApps.forEach(app => {
        container.appendChild(this._createAppCard(app));
      });
    }
  }

  _renderAllAppsSection() {
    const container = document.getElementById('all-apps');
    if (!container) return;

    // Check if there are already server-rendered cards
    const existingCards = container.querySelectorAll('.app-card-wrapper');
    if (existingCards.length > 0) {
      // Enhance existing cards with favorite state
      existingCards.forEach(card => {
        const appId = card.dataset.appId;
        const favoriteBtn = card.querySelector('.favorite-btn');
        if (favoriteBtn && appId) {
          const isFavorite = this.favoritesController.isFavorite(appId);
          favoriteBtn.classList.toggle('is-favorite', isFavorite);
          const icon = favoriteBtn.querySelector('i');
          if (icon) {
            icon.className = isFavorite ? 'bi bi-star-fill' : 'bi bi-star';
          }
        }
      });
      return;
    }

    // No server-rendered cards, render from JS data
    if (this.apps.length === 0) {
      const emptyState = this._createEmptyState('bi-grid-3x3-gap', 'No applications available', '');
      container.appendChild(emptyState);
    } else {
      this.apps.forEach(app => {
        container.appendChild(this._createAppCard(app));
      });
    }
  }

  _createAppCard(app) {
    if (!this.appCardTemplate) return null;

    const isFavorite = this.favoritesController.isFavorite(app.sso_url);
    const imageUrl = this._getImageUrl(app);
    const clone = this.appCardTemplate.content.cloneNode(true);

    const link = clone.querySelector('a');
    link.href = app.formatted_sso_url || app.sso_url;
    link.dataset.appId = app.sso_url;
    link.dataset.appName = app.name;

    const favoriteBtn = clone.querySelector('.favorite-btn');
    favoriteBtn.dataset.appId = app.sso_url;
    if (isFavorite) {
      favoriteBtn.classList.add('is-favorite');
      favoriteBtn.setAttribute('aria-label', 'Remove from favorites');
    }

    const icon = favoriteBtn.querySelector('i');
    icon.className = isFavorite ? 'bi bi-star-fill' : 'bi bi-star';

    const appIcon = clone.querySelector('.portal-app-icon');
    appIcon.style.backgroundImage = `url(${imageUrl})`;

    const appName = clone.querySelector('.portal-app-name');
    appName.textContent = app.name;

    return clone;
  }

  _createEmptyState(iconClass, message, hint) {
    if (!this.emptyStateTemplate) return null;

    const clone = this.emptyStateTemplate.content.cloneNode(true);

    const icon = clone.querySelector('.portal-empty-icon i');
    icon.className = `bi ${iconClass}`;

    const messageEl = clone.querySelector('p');
    messageEl.textContent = message;

    const hintEl = clone.querySelector('.portal-empty-hint');
    if (hint) {
      hintEl.textContent = hint;
    } else {
      hintEl.remove();
    }

    return clone;
  }

  _getImageUrl(app) {
    // Has custom image
    if (app.image_url) {
      return app.image_url;
    }
    // Core apps use SA logo
    if (app.type === APP_TYPE_CORE) {
      return this._getDefaultLogoUrl(APP_TYPE_CORE);
    }
    // Try favicon from domain
    const faviconUrl = this._getFaviconUrl(app.sso_url || app.formatted_sso_url);
    if (faviconUrl) {
      return faviconUrl;
    }
    // Final fallback
    return this._getDefaultLogoUrl('generic');
  }

  _getFaviconUrl(ssoUrl) {
    if (!ssoUrl || !this.faviconServiceUrl) return null;
    try {
      const url = new URL(ssoUrl);
      const iconUrl = new URL(this.faviconServiceUrl);
      iconUrl.searchParams.append('domain', url.hostname);
      iconUrl.searchParams.append('sz', this.faviconSize);
      return iconUrl.toString();
    } catch {
      return null;
    }
  }

  _getDefaultLogoUrl(type) {
    // Use the asset paths from the page data attributes
    const dataEl = document.getElementById('portal-data');
    if (dataEl) {
      if (type === APP_TYPE_CORE && dataEl.dataset.defaultLogoSa) {
        return dataEl.dataset.defaultLogoSa;
      }
      if (dataEl.dataset.defaultLogoGeneric) {
        return dataEl.dataset.defaultLogoGeneric;
      }
    }
    // Fallback paths
    return type === APP_TYPE_CORE
      ? `${DEFAULT_LOGO_PATH}/application-logo-sa.svg`
      : `${DEFAULT_LOGO_PATH}/application-logo-generic.svg`;
  }

  _handleFavoritesChange(favorites, appId, added) {
    // Re-render the favorites section
    this._renderFavoritesSection();
  }

  _handleRecentlyUsedChange(recentlyUsed, appId) {
    // Re-render recently used section
    this._renderRecentlyUsedSection();
  }
}

// Initialize when DOM is ready
function initPortal() {
  const portalApp = new PortalApp();
  portalApp.init().catch(err => {
    console.error('Failed to initialize portal:', err);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPortal);
} else {
  initPortal();
}

export { PortalApp };
